import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { useAppDemo } from '../../context/AppDemoContext';
import confetti from 'canvas-confetti';

/**
 * BuildingApp component - completely redesigned with an elegant, modern look
 * that simulates creating an app based on user's selections
 */
const BuildingApp = ({ registerValidation }) => {
    const { 
        appName, 
        primaryColor, 
        secondaryColor,
        bookQueries,
        bookTitles,
        splashVideo,
        brandData,
        nextStep,
        goToStep
    } = useAppDemo();

    // Main animation state
    const [progress, setProgress] = useState(0);
    const [phase, setPhase] = useState('preparing'); // preparing, building, finalizing, completed
    const [isComplete, setIsComplete] = useState(false);
    
    // Refs to manage animations
    const animationRef = useRef(null);
    const isMounted = useRef(true);
    
    // Add a ref to track if phase change is in progress to prevent infinite loops
    const phaseTransitionRef = useRef(false);
    const progressRef = useRef(0);
    
    // Display values
    const displayName = appName || 'Your App';
    const themeColor = primaryColor || '#1e9963';
    const bookCount = bookTitles?.length || (bookQueries?.length * 3) || 5;
    const hasVideo = splashVideo && Object.keys(splashVideo).length > 0;
    const hasLogo = brandData?.logos && brandData.logos.length > 0;

    // Register validation with wizard
    useEffect(() => {
        if (registerValidation) {
            registerValidation('building-app', true);
        }
    }, [registerValidation]);

    // Clean up on unmount
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, []);
    
    // Helper function to safely check if we should proceed with a phase change
    // This helps prevent the infinite loop when floating point precision issues occur
    const shouldTransitionPhase = (currentPhase, newPhase, currentProgress, targetProgress) => {
        // Skip if already transitioning
        if (phaseTransitionRef.current) return false;
        
        // Skip if the phases are the same
        if (currentPhase === newPhase) return false;
        
        // Check if we're within 0.1% of the target - this fixes floating point precision issues
        const progressDiff = Math.abs(currentProgress - targetProgress);
        if (progressDiff > 0.1) return false;
        
        return true;
    };
    
    // Animation function using requestAnimationFrame for smoother performance
    const animateProgress = useCallback((targetProgress, duration, onComplete) => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }
        
        // Store target progress rounded to 2 decimal places to avoid floating point precision issues
        const roundedTarget = Math.round(targetProgress * 100) / 100;
        
        // If we're already at the target (within 0.1%), skip animation
        if (Math.abs(progressRef.current - roundedTarget) < 0.1) {
            console.log(`BuildingApp: Skipping animation - already at target (${progressRef.current}%)`);
            if (onComplete && typeof onComplete === 'function') {
                onComplete();
            }
            return;
        }
        
        const startTime = performance.now();
        const startProgress = progressRef.current; // Use the ref value for consistency
        
        console.log(`BuildingApp: Starting animation from ${startProgress}% to ${roundedTarget}% over ${duration}ms`);
        
        const updateProgress = (currentTime) => {
            if (!isMounted.current) {
                console.log('BuildingApp: Animation stopped - component not mounted');
                return;
            }
            
            const elapsedTime = currentTime - startTime;
            const progressFraction = Math.min(elapsedTime / duration, 1);
            
            // Use easeOutCubic for smoother animation
            const easedProgress = 1 - Math.pow(1 - progressFraction, 3);
            const newProgress = startProgress + (roundedTarget - startProgress) * easedProgress;
            
            // Round to 2 decimal places to avoid floating point issues
            const roundedProgress = Math.round(newProgress * 100) / 100;
            
            // Update the ref value first
            progressRef.current = roundedProgress;
            // Then update state (causes re-render)
            setProgress(roundedProgress);
            
            if (progressFraction < 1) {
                animationRef.current = requestAnimationFrame(updateProgress);
            } else if (onComplete && typeof onComplete === 'function') {
                console.log(`BuildingApp: Animation reached target ${roundedTarget}%`);
                onComplete();
            }
        };
        
        animationRef.current = requestAnimationFrame(updateProgress);
    }, []); // Remove progress from dependency array to avoid re-creation of this function
    
    // Phase management - controlled sequence of build phases
    useEffect(() => {
        if (!isMounted.current) return;
        
        // Debug phase change
        console.log(`BuildingApp: Phase changed to ${phase}`);
        
        // Reset progress when phase changes
        if (phase === 'preparing') {
            console.log('BuildingApp: Starting preparing phase - should take 2.5s');
            // Initial preparation phase (0-20%) - reduced from 4000ms to 2500ms
            animateProgress(20, 2500, () => {
                console.log('BuildingApp: Preparing phase complete');
                
                if (isMounted.current && shouldTransitionPhase(phase, 'building', progressRef.current, 20)) {
                    phaseTransitionRef.current = true; // Lock transitions
                    setPhase('building');
                    setTimeout(() => {
                        phaseTransitionRef.current = false; // Unlock after state update completes
                    }, 50);
                }
            });
        } else if (phase === 'building') {
            console.log('BuildingApp: Starting building phase - should take 8s');
            // Main building phase (20-70%) - reduced from 12000ms to 8000ms
            animateProgress(70, 8000, () => {
                console.log('BuildingApp: Building phase complete');
                
                if (isMounted.current && shouldTransitionPhase(phase, 'finalizing', progressRef.current, 70)) {
                    phaseTransitionRef.current = true; // Lock transitions
                    setPhase('finalizing');
                    setTimeout(() => {
                        phaseTransitionRef.current = false; // Unlock after state update completes
                    }, 50);
                }
            });
        } else if (phase === 'finalizing') {
            console.log('BuildingApp: Starting finalizing phase - should take 4s');
            // Finalizing phase (70-98%) - reduced from 6000ms to 4000ms
            animateProgress(98, 4000, () => {
                console.log('BuildingApp: Finalizing phase complete');
                
                if (isMounted.current && shouldTransitionPhase(phase, 'completed', progressRef.current, 98)) {
                    phaseTransitionRef.current = true; // Lock transitions
                    setPhase('completed');
                    setTimeout(() => {
                        phaseTransitionRef.current = false; // Unlock after state update completes
                    }, 50);
                }
            });
        } else if (phase === 'completed') {
            console.log('BuildingApp: Starting completed phase - should take 1s');
            // Complete the progress and trigger completion - kept at 1000ms
            animateProgress(100, 1000, () => {
                console.log('BuildingApp: Animation sequence complete');
                
                if (isMounted.current && !isComplete) {
                    setIsComplete(true);
                    // Trigger confetti for a celebration effect
                    confetti({
                        particleCount: 150,
                        spread: 70,
                        origin: { y: 0.6 }
                    });
                }
            });
        }
    }, [phase, animateProgress, isComplete]);
    
    // Sync progress state with ref for easier access
    useEffect(() => {
        progressRef.current = progress;
    }, [progress]);
    
    // Handle next step button click
    const handleContinue = () => {
        if (goToStep) {
            // Use goToStep to navigate directly to the App Preview slide (index 7)
            goToStep(7);
        }
    };
    
    // Handle skip functionality
    const handleSkip = () => {
        console.log('BuildingApp: Skip button clicked');
        
        // Cancel any ongoing animation
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }
        
        // Force completion state
        setProgress(100);
        setPhase('completed');
        setIsComplete(true);
        
        // Trigger confetti for a celebration effect
        confetti({
            particleCount: 150,
            spread: 70,
            origin: { y: 0.6 }
        });
    };
    
    // Helper functions for phase display
    const getPhaseIcon = () => {
        if (isComplete) {
            return <CheckCircle className="text-white w-8 h-8" />;
        }
        
        return (
            <svg className="w-8 h-8 text-white" viewBox="0 0 24 24">
                <circle 
                    className="opacity-25" 
                    cx="12" 
                    cy="12" 
                    r="10" 
                    stroke="currentColor" 
                    strokeWidth="4"
                />
                <path 
                    className="opacity-75" 
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
            </svg>
        );
    };
    
    const getPhaseText = () => {
        if (isComplete) {
            return `${displayName} has been successfully built!`;
        }
        
        switch (phase) {
            case 'preparing':
                return `Preparing to build ${displayName}...`;
            case 'building':
                return `Building core components for ${displayName}...`;
            case 'finalizing':
                return `Finalizing and optimizing ${displayName}...`;
            case 'completed':
                return `Completing ${displayName} build...`;
            default:
                return `Building ${displayName}...`;
        }
    };
    
    const getPhaseDescription = () => {
        if (isComplete) {
            return "Your custom app is ready to preview.";
        }
        
        switch (phase) {
            case 'preparing':
                return "Setting up environment and initializing resources...";
            case 'building':
                return `Integrating content and constructing ${hasLogo ? 'branded' : 'custom'} UI components...`;
            case 'finalizing':
                return `Optimizing performance and preparing for deployment...`;
            case 'completed':
                return "Running final checks...";
            default:
                return "Please wait while we create your custom app...";
        }
    };
    
    // Building details based on user selections
    const getBuildingDetails = () => {
        return [
            { 
                label: "App Name", 
                value: displayName 
            },
            { 
                label: "Theme Color", 
                value: <div className="w-4 h-4 rounded-full" style={{ backgroundColor: themeColor }}></div>
            },
            { 
                label: "Content", 
                value: `${bookCount} Books${hasVideo ? ' + Video' : ''}`
            },
            { 
                label: "Brand Elements", 
                value: hasLogo ? "Custom Logo & Colors" : "Custom Colors" 
            }
        ];
    };

    return (
        <div className="max-w-3xl mx-auto">
            {/* Modern, elegant design with glass-morphism */}
            <div className="bg-white bg-opacity-10 backdrop-filter backdrop-blur-lg rounded-xl border border-white border-opacity-20 shadow-xl overflow-hidden">
                {/* Header with animated progress */}
                <div className="relative h-48 flex items-center justify-center overflow-hidden">
                    {/* Dynamic background gradient based on selected colors */}
                    <div 
                        className="absolute inset-0 z-0 transition-all duration-1000"
                        style={{ 
                            background: `linear-gradient(135deg, ${themeColor}99 0%, ${secondaryColor || themeColor}99 100%)`,
                            opacity: progress / 100
                        }}
                    ></div>
                    
                    {/* Animated circles in background */}
                    <div className="absolute inset-0 z-0 overflow-hidden">
                        <div 
                            className="absolute w-64 h-64 rounded-full"
                            style={{ 
                                background: `radial-gradient(circle, ${themeColor}55 0%, transparent 70%)`,
                                top: '-20%',
                                left: '-10%',
                                transform: `scale(${0.5 + progress/200})`,
                                opacity: 0.7
                            }}
                        ></div>
                        <div 
                            className="absolute w-72 h-72 rounded-full"
                            style={{ 
                                background: `radial-gradient(circle, ${secondaryColor || themeColor}55 0%, transparent 70%)`,
                                bottom: '-30%',
                                right: '-15%',
                                transform: `scale(${0.6 + progress/200})`,
                                opacity: 0.5
                            }}
                        ></div>
                    </div>
                    
                    {/* Content overlay */}
                    <div className="relative z-10 text-center p-6">
                        {/* Animated progress circle */}
                        <div className="relative mx-auto mb-4 w-16 h-16 flex items-center justify-center">
                            <svg className="w-16 h-16 absolute" viewBox="0 0 100 100">
                                <circle 
                                    className="text-gray-300 opacity-20" 
                                    cx="50" 
                                    cy="50" 
                                    r="40" 
                                    stroke="currentColor" 
                                    strokeWidth="8"
                                    fill="none"
                                />
                                <circle 
                                    className="text-white" 
                                    cx="50" 
                                    cy="50" 
                                    r="40" 
                                    stroke="currentColor" 
                                    strokeWidth="8"
                                    strokeLinecap="round"
                                    fill="none"
                                    strokeDasharray={Math.PI * 80}
                                    strokeDashoffset={Math.PI * 80 * (1 - progress / 100)}
                                    transform="rotate(-90 50 50)"
                                    style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }}
                                />
                            </svg>
                            
                            <div className="absolute inset-0 flex items-center justify-center">
                                {isComplete ? (
                                    <CheckCircle className="w-8 h-8 text-white" />
                                ) : (
                                    <span className="text-white text-sm font-medium">{Math.round(progress)}%</span>
                                )}
                            </div>
                        </div>
                        
                        <h2 className="text-xl font-bold text-white mb-1 transition-all">
                            {getPhaseText()}
                        </h2>
                        <p className="text-white text-opacity-90 text-sm max-w-md mx-auto">
                            {getPhaseDescription()}
                        </p>
                    </div>
                        </div>
                        
                {/* Build details section */}
                <div className="bg-white bg-opacity-5 border-t border-white border-opacity-10 p-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {getBuildingDetails().map((detail, index) => (
                            <div key={index} className="text-center">
                                <p className="text-white text-opacity-60 text-xs mb-1">{detail.label}</p>
                                <div className="text-white font-medium flex items-center justify-center">
                                    {typeof detail.value === 'string' ? detail.value : detail.value}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                
                {/* Dynamic build stages visualization */}
                <div className="p-6">
                    <div className="space-y-4">
                        {/* Show build phases with dynamic completion */}
                        <BuildPhase 
                            title="Environment Setup"
                            status={progress >= 10 ? 'completed' : 'in-progress'}
                            color={themeColor}
                        />
                        
                        <BuildPhase 
                            title="Branding & UI Elements"
                            status={progress >= 30 ? (progress >= 35 ? 'completed' : 'in-progress') : 'pending'}
                            color={themeColor}
                        />
                        
                        <BuildPhase 
                            title="Content Integration" 
                            description={`Integrating ${bookCount} books${hasVideo ? ' and video content' : ''}`}
                            status={progress >= 50 ? (progress >= 60 ? 'completed' : 'in-progress') : 'pending'}
                            color={themeColor}
                        />
                        
                        <BuildPhase 
                            title="Core Functionality"
                            status={progress >= 70 ? (progress >= 80 ? 'completed' : 'in-progress') : 'pending'}
                            color={themeColor}
                        />
                        
                        <BuildPhase 
                            title="Optimization & Finalization"
                            status={progress >= 90 ? (progress >= 98 ? 'completed' : 'in-progress') : 'pending'}
                            color={themeColor}
                        />
                            </div>
                        </div>
                        
                {/* Completion button */}
                {isComplete ? (
                    <div className="p-6 bg-white bg-opacity-5 border-t border-white border-opacity-10 text-center">
                        <button 
                            onClick={handleContinue}
                            className="inline-flex items-center px-6 py-3 rounded-lg text-white shadow-md transition-all duration-300 hover:shadow-lg"
                            style={{ 
                                background: `linear-gradient(90deg, ${themeColor} 0%, ${secondaryColor || themeColor} 100%)`,
                            }}
                        >
                            <span>See your Prototype App</span>
                            <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                ) : (
                    <div className="p-6 bg-white bg-opacity-5 border-t border-white border-opacity-10 text-center">
                        <button 
                            onClick={handleSkip}
                            className="text-white/70 text-sm underline hover:text-white"
                        >
                            Skip animation
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// BuildPhase component for cleaner code organization
const BuildPhase = ({ title, description, status, color }) => {
    const getStatusIcon = () => {
        switch (status) {
            case 'completed':
                return <CheckCircle className="w-5 h-5 text-green-500" />;
            case 'in-progress':
                return (
                    <div className="w-5 h-5 rounded-full border-2 border-current border-r-transparent animate-spin"
                        style={{ color }}
                    ></div>
                );
            case 'pending':
                return <div className="w-5 h-5 rounded-full border border-white border-opacity-20"></div>;
            default:
                return null;
        }
    };
    
    return (
        <div className={`flex items-center p-3 rounded-lg transition-all ${
            status === 'completed' 
                ? 'bg-green-500 bg-opacity-10 border border-green-500 border-opacity-20' 
                : status === 'in-progress'
                    ? 'bg-white bg-opacity-10 border border-white border-opacity-20'
                    : 'bg-white bg-opacity-5 border border-white border-opacity-10'
        }`}>
            <div className="flex-shrink-0 mr-3">
                {getStatusIcon()}
            </div>
            
            <div className="flex-grow">
                <div className="flex items-center justify-between">
                    <p className={`font-medium text-sm ${
                        status === 'completed' ? 'text-white' : status === 'in-progress' ? 'text-white' : 'text-white text-opacity-60'
                    }`}>
                        {title}
                    </p>
                    
                    {status === 'in-progress' && (
                        <span className="text-xs text-white text-opacity-80">In Progress</span>
                    )}
                </div>
                
                {description && (
                    <p className="text-white text-opacity-60 text-xs mt-1">{description}</p>
            )}
            </div>
        </div>
    );
};

export default BuildingApp; 